import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Trophy, Medal, Award, Clock, User, Phone, Mail, BookOpen, CheckCircle, XCircle, MinusCircle, ChevronDown, ChevronUp, Download, FileText as FileTextIcon } from 'lucide-react';
import api from '../../lib/api';
import LaTeXRenderer from '../../components/LaTeXRenderer';
import { toast } from 'sonner';

function TestRankings() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [rankings, setRankings] = useState([]);
  const [testInfo, setTestInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [expandedAttempt, setExpandedAttempt] = useState(null);

  useEffect(() => {
    fetchTests();
  }, []);

  useEffect(() => {
    if (selectedTest) {
      fetchRankings(selectedTest);
    }
  }, [selectedTest]);

  const fetchTests = async () => {
    try {
      const response = await api.get('/tests');
      // Show all tests for rankings view
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to fetch tests');
    }
  };

  const fetchRankings = async (testId) => {
    setLoading(true);
    try {
      const response = await api.get(`/rankings/test/${testId}`);
      setTestInfo(response.data.test);
      setRankings(response.data.rankings);
    } catch (error) {
      console.error('Error fetching rankings:', error);
      toast.error('Failed to fetch rankings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetails = async (userId, testId) => {
    setDetailsLoading(true);
    try {
      const response = await api.get(`/rankings/student/${userId}/test/${testId}`);
      setStudentDetails(response.data);
      setShowDetails(true);
    } catch (error) {
      console.error('Error fetching student details:', error);
      toast.error('Failed to fetch student details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    fetchStudentDetails(student.user_id, selectedTest);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m ${secs}s`;
    }
    return `${mins}m ${secs}s`;
  };

  const exportExcel = async () => {
    if (!selectedTest) return;
    try {
      const response = await api.get(`/rankings/test/${selectedTest}/export/excel`);
      const { data, filename } = response.data;
      const byteChars = atob(data);
      const byteNumbers = new Array(byteChars.length).fill(0).map((_, i) => byteChars.charCodeAt(i));
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const exportPDF = async () => {
    if (!selectedTest) return;
    try {
      const response = await api.get(`/rankings/test/${selectedTest}/export/pdf`);
      const { data, filename } = response.data;
      const byteChars = atob(data);
      const byteNumbers = new Array(byteChars.length).fill(0).map((_, i) => byteChars.charCodeAt(i));
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-gray-600 font-bold">{rank}</span>;
  };

  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (rank === 2) return 'bg-gray-100 text-gray-800 border-gray-300';
    if (rank === 3) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-blue-50 text-blue-800 border-blue-200';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Test Rankings</h1>
            <p className="text-gray-600">View rankings for all students who have taken a test</p>
          </div>
          {rankings.length > 0 && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={exportPDF} data-testid="export-rankings-pdf">
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" onClick={exportExcel} data-testid="export-rankings-excel">
                <Download className="w-4 h-4 mr-2" />
                Excel
              </Button>
            </div>
          )}
        </div>

        {/* Test Selector */}
        <Card className="p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Test</label>
          <select
            value={selectedTest}
            onChange={(e) => setSelectedTest(e.target.value)}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Select a Test --</option>
            {tests.map((test) => (
              <option key={test.id} value={test.id}>
                {test.title}
              </option>
            ))}
          </select>
        </Card>

        {/* Test Info */}
        {testInfo && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center gap-4">
              <BookOpen className="w-8 h-8 text-blue-600" />
              <div>
                <h3 className="font-semibold text-blue-900">{testInfo.title}</h3>
                <p className="text-sm text-blue-700">
                  {testInfo.question_ids?.length || testInfo.total_questions || 0} Questions • {testInfo.config?.duration || testInfo.duration_minutes || 0} Minutes • {rankings.length} Students Attempted
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Rankings Table */}
        {loading ? (
          <Card className="p-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading rankings...</p>
          </Card>
        ) : rankings.length > 0 ? (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700 w-16">Rank</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Student</th>
                    <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Contact</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Type</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Score</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Percentage</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Duration</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Trust Score</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Gaze Score</th>
                    <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Attempts</th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((student) => (
                    <tr 
                      key={student.user_id} 
                      className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => handleStudentClick(student)}
                    >
                      <td className="py-4 px-4 text-center">
                        <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${getRankBadgeColor(student.rank)}`}>
                          {getRankIcon(student.rank)}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-900 hover:text-blue-600">{student.user_name}</div>
                        {student.batch_name && (
                          <span className="text-xs text-gray-500">Batch: {student.batch_name}</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {student.user_email}
                          </div>
                          {student.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {student.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        {student.registration_type === 'public_mock_imu_cet' ? (
                          <Badge className="bg-green-100 text-green-800">Public</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-800">Batch</Badge>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center font-semibold text-gray-900">
                        {student.best_score}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`font-bold ${
                          student.best_percentage >= 80 ? 'text-green-600' :
                          student.best_percentage >= 60 ? 'text-blue-600' :
                          student.best_percentage >= 40 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {student.best_percentage?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center text-sm text-gray-600">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDuration(student.duration_seconds)}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        {student.trust_score != null ? (
                          <span className={`font-medium px-2 py-0.5 rounded text-sm ${
                            student.trust_score >= 80 ? 'bg-green-100 text-green-700' :
                            student.trust_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            student.trust_score >= 40 ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {Math.round(student.trust_score)}%
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {student.gaze_score != null ? (
                          <span className={`font-medium px-2 py-0.5 rounded text-sm ${
                            student.gaze_score >= 80 ? 'bg-green-100 text-green-700' :
                            student.gaze_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            student.gaze_score >= 40 ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {Math.round(student.gaze_score)}%
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">N/A</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Badge variant="outline">{student.attempts_count}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : selectedTest ? (
          <Card className="p-12 text-center">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Rankings Yet</h3>
            <p className="text-gray-500">No students have completed this test yet.</p>
          </Card>
        ) : (
          <Card className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Test</h3>
            <p className="text-gray-500">Choose a test from the dropdown to view student rankings.</p>
          </Card>
        )}

        {/* Student Details Modal */}
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Student Test Details
              </DialogTitle>
            </DialogHeader>
            
            {detailsLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading details...</p>
              </div>
            ) : studentDetails && (
              <div className="space-y-6">
                {/* Student Info */}
                <Card className="p-4 bg-gray-50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-semibold">{studentDetails.user.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-semibold">{studentDetails.user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-semibold">{studentDetails.user.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Type</p>
                      <p className="font-semibold">
                        {studentDetails.user.registration_type === 'public_mock_imu_cet' ? 'Public Registration' : 
                         studentDetails.user.batch_name || 'Batch Student'}
                      </p>
                    </div>
                    {studentDetails.user.course_interest && (
                      <div>
                        <p className="text-sm text-gray-500">Course Interest</p>
                        <p className="font-semibold">
                          {studentDetails.user.course_interest === 'btech' ? 'B.Tech Marine Engineering' : 'BSc Nautical Science'}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Test Info */}
                <Card className="p-4 bg-blue-50">
                  <h4 className="font-semibold text-blue-900 mb-2">{studentDetails.test.title}</h4>
                  <p className="text-sm text-blue-700">
                    {studentDetails.test.total_questions} Questions • {studentDetails.test.total_points} Total Points
                  </p>
                </Card>

                {/* Attempts */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Attempts ({studentDetails.attempts.length})</h4>
                  
                  {studentDetails.attempts.map((attempt, idx) => (
                    <Card key={attempt.attempt_id} className="overflow-hidden">
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                        onClick={() => setExpandedAttempt(expandedAttempt === idx ? null : idx)}
                      >
                        <div className="flex items-center gap-4">
                          <Badge className={attempt.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            Attempt {studentDetails.attempts.length - idx}
                          </Badge>
                          <div className="flex items-center gap-6 text-sm">
                            <span className="font-semibold text-lg">{attempt.percentage?.toFixed(1)}%</span>
                            <span className="text-gray-600">Score: {attempt.score}/{studentDetails.test.total_points}</span>
                            <span className="text-gray-600 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatDuration(attempt.duration_seconds)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> {attempt.correct_answers}
                            </span>
                            <span className="text-red-600 flex items-center gap-1">
                              <XCircle className="w-4 h-4" /> {attempt.wrong_answers}
                            </span>
                            <span className="text-gray-400 flex items-center gap-1">
                              <MinusCircle className="w-4 h-4" /> {attempt.unanswered}
                            </span>
                          </div>
                          {expandedAttempt === idx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                      
                      {expandedAttempt === idx && (
                        <div className="border-t p-4 bg-gray-50 max-h-96 overflow-y-auto">
                          <h5 className="font-medium mb-3">Question-wise Results</h5>
                          <div className="space-y-2">
                            {attempt.question_results.map((q, qIdx) => (
                              <div 
                                key={q.question_id} 
                                className={`p-3 rounded-lg border ${
                                  q.is_correct ? 'bg-green-50 border-green-200' : 
                                  q.user_answer === null ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">Q{qIdx + 1}:</p>
                                    <div className="text-sm mt-1">
                                      <LaTeXRenderer text={q.question_text?.substring(0, 150) + (q.question_text?.length > 150 ? '...' : '')} />
                                    </div>
                                    <div className="mt-1 text-xs space-y-1">
                                      <p className="text-gray-600">
                                        Your Answer: <span className={q.is_correct ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                          {q.user_answer !== null ? (Array.isArray(q.user_answer) ? q.user_answer.join(', ') : q.user_answer) : 'Not Answered'}
                                        </span>
                                      </p>
                                      {!q.is_correct && q.user_answer !== null && (
                                        <p className="text-gray-600">
                                          Correct Answer: <span className="text-green-600 font-medium">
                                            {Array.isArray(q.correct_answer) ? q.correct_answer.join(', ') : q.correct_answer}
                                          </span>
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0">
                                    {q.is_correct ? (
                                      <CheckCircle className="w-5 h-5 text-green-600" />
                                    ) : q.user_answer === null ? (
                                      <MinusCircle className="w-5 h-5 text-gray-400" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-red-600" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default TestRankings;
